import { computed, shallowRef, watch } from 'vue';

import { useAuthContext } from '@shared/context/useAuthContext';
import { ProcessOperationsService, type ProcessRolloutState } from '../api';
import type { ProcessPermission } from '../utils/processPermissions';
import { resolveProcessPermissions } from '../utils/processPermissions';

const rollout = shallowRef<ProcessRolloutState | null>(null);
const rolloutLoading = shallowRef(false);
const rolloutError = shallowRef<string | null>(null);
let rolloutPromise: Promise<void> | null = null;
let loadedAuthKey: string | null = null;

const toAuthKey = (token: string | null, roles: readonly string[] | undefined) => {
  if (!token) return null;
  return `${token}:${(roles ?? []).join(',')}`;
};

const loadProcessRollout = async (authKey: string) => {
  if (rolloutPromise && loadedAuthKey === authKey) {
    return rolloutPromise;
  }

  loadedAuthKey = authKey;
  rolloutLoading.value = true;
  rolloutError.value = null;

  rolloutPromise = ProcessOperationsService.getRollout()
    .then((state) => {
      if (loadedAuthKey === authKey) {
        rollout.value = state;
      }
    })
    .catch((error: unknown) => {
      if (loadedAuthKey === authKey) {
        rollout.value = null;
        rolloutError.value = error instanceof Error ? error.message : 'Не удалось загрузить права процессов';
      }
    })
    .finally(() => {
      if (loadedAuthKey === authKey) {
        rolloutLoading.value = false;
        rolloutPromise = null;
      }
    });

  return rolloutPromise;
};

export const clearProcessRolloutCache = () => {
  loadedAuthKey = null;
  rollout.value = null;
  rolloutLoading.value = false;
  rolloutError.value = null;
  rolloutPromise = null;
};


export const ensureProcessRollout = async (
  token: string | null,
  roles: readonly string[] | undefined
): Promise<ProcessRolloutState | null> => {
  const authKey = toAuthKey(token, roles);
  if (!authKey) {
    clearProcessRolloutCache();
    return null;
  }

  if (authKey === loadedAuthKey && rollout.value) {
    return rollout.value;
  }

  await loadProcessRollout(authKey);
  return rollout.value;
};

export const useProcessPermissions = () => {
  const auth = useAuthContext();

  const roles = computed(() => auth.user?.roles ?? []);
  const authKey = computed(() => toAuthKey(auth.token, roles.value));

  watch(
    authKey,
    (currentAuthKey) => {
      if (!currentAuthKey) {
        clearProcessRolloutCache();
        return;
      }

      void ensureProcessRollout(auth.token, roles.value);
    },
    { immediate: true }
  );

  const matrix = computed(() => rollout.value?.matrix ?? []);
  const permissions = computed(() =>
    rollout.value?.currentUser.permissions ?? resolveProcessPermissions(roles.value, matrix.value)
  );
  const rolloutEnabled = computed(() => Boolean(rollout.value));
  const allPermissions = computed(() => matrix.value.map((entry) => entry.permission));

  const can = (permission: ProcessPermission) =>
    computed(() => Boolean(permissions.value[permission]));

  return {
    roles,
    rollout,
    rolloutLoading,
    rolloutError,
    rolloutEnabled,
    permissions,
    allPermissions,
    can,
    canViewBuilder: can('process.view'),
    canEditDraft: can('process.editDraft'),
    canPublish: can('process.publish'),
    canLaunch: can('process.launch'),
    canExecuteTask: can('process.executeTask'),
    canViewAnalytics: can('process.viewAnalytics'),
    canViewAudit: can('process.viewAudit'),
    canAdjustInventory: can('process.adjustInventory')
  };
};
